/**
 * ============================================================================
 * [P3-01-F] TextCardActions - 카드 액션 버튼 컴포넌트
 *
 * 역할:
 * - 즐겨찾기, 복사, 편집, 삭제 액션 버튼 제공
 * - memo로 불필요한 리렌더링 방지
 *
 * Props:
 * - isFavorite: 즐겨찾기 상태
 * - onToggleFavorite: 즐겨찾기 토글 핸들러
 * - onCopy: 클립보드 복사 핸들러
 * - onEdit: 편집 (상세 모달 열기) 핸들러
 * - onDelete: 삭제 핸들러
 * ============================================================================
 */

import { memo } from 'react'
import styles from './Saved.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TextCardActionsProps {
  isFavorite?: boolean
  onToggleFavorite: () => void
  onCopy: () => void
  onEdit: () => void
  onDelete: () => void
}

// ============================================================================
// Component
// ============================================================================
export const TextCardActions = memo(function TextCardActions({
  isFavorite,
  onToggleFavorite,
  onCopy,
  onEdit,
  onDelete,
}: TextCardActionsProps) {
  // ========================================================================
  // 이벤트 버블링 방지 래퍼
  // - 카드 클릭과 버튼 클릭 이벤트 분리
  // ========================================================================
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleFavorite()
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCopy()
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit()
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.cardActions}>
      {/* [P3-01] 즐겨찾기 버튼 */}
      <button
        className={`${styles.actionBtn} ${isFavorite ? styles.favoriteActive : ''}`}
        onClick={handleToggleFavorite}
        title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
      >
        {isFavorite ? '⭐' : '☆'}
      </button>

      {/* 복사 버튼 */}
      <button
        className={styles.actionBtn}
        onClick={handleCopy}
        title="클립보드에 복사"
        aria-label="클립보드에 복사"
      >
        📋
      </button>

      {/* 편집 버튼 */}
      <button
        className={styles.actionBtn}
        onClick={handleEdit}
        title="편집기로 불러오기"
        aria-label="편집기로 불러오기"
      >
        ✏️
      </button>

      {/* 삭제 버튼 (카드 전용 .actionBtnDanger — 평상시 회색, hover에만 옅은 빨강) */}
      <button
        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
        onClick={handleDelete}
        title="삭제"
        aria-label="삭제"
      >
        🗑️
      </button>
    </div>
  )
})

export default TextCardActions
